/**
 * @file     distributedRateLimiter.ts
 * @readme   ./CLAUDE.md
 * @purpose  Distributed IP-based rate limiter backed by the Supabase Postgres rate_limit_check RPC. Survives across Vercel Fluid Compute function instances. Falls back to an in-memory limiter when the admin client is unavailable.
 */
import { getSupabaseAdmin } from '@/core/supabase/admin';

export interface DistributedRateLimitResult {
  allowed: boolean;
  count: number;
  retryAfterSeconds: number;
}

interface InMemoryEntry {
  count: number;
  windowStart: number;
}

const fallbackBuckets = new Map<string, InMemoryEntry>();

function fallbackCheck(
  bucketKey: string,
  ip: string,
  windowMs: number,
  limit: number,
): DistributedRateLimitResult {
  const mapKey = `${bucketKey}::${ip}`;
  const now = Date.now();
  const entry = fallbackBuckets.get(mapKey);

  if (!entry || now - entry.windowStart > windowMs) {
    fallbackBuckets.set(mapKey, { count: 1, windowStart: now });
    return {
      allowed: true,
      count: 1,
      retryAfterSeconds: Math.ceil(windowMs / 1000),
    };
  }

  entry.count += 1;
  const elapsedMs = now - entry.windowStart;
  const retryAfterSeconds = Math.max(0, Math.ceil((windowMs - elapsedMs) / 1000));
  return {
    allowed: entry.count <= limit,
    count: entry.count,
    retryAfterSeconds,
  };
}

interface RateLimitRpcRow {
  allowed: boolean;
  current_count: number;
  retry_after_seconds: number;
}

/**
 * Atomic increment-or-create against the Supabase rate_limit_buckets table.
 * If the admin client is missing or the RPC errors, falls back to a per-process
 * in-memory bucket so the user flow is never blocked by limiter outages.
 */
export async function checkDistributedRateLimit(
  bucketKey: string,
  bucketType: string,
  ip: string,
  windowMs: number,
  limit: number,
): Promise<DistributedRateLimitResult> {
  const admin = getSupabaseAdmin();
  if (!admin) {
    return fallbackCheck(bucketKey, ip, windowMs, limit);
  }

  const windowSeconds = Math.max(1, Math.ceil(windowMs / 1000));

  try {
    // The shared admin client is untyped (no Database generic), so its `rpc`
    // signature requires an explicit cast for typed RPC calls.
    const rpc = admin.rpc as unknown as (
      fn: 'rate_limit_check',
      args: {
        p_bucket_key: string;
        p_bucket_type: string;
        p_ip: string;
        p_window_seconds: number;
        p_limit: number;
      },
    ) => Promise<{ data: RateLimitRpcRow[] | null; error: unknown }>;

    const { data, error } = await rpc.call(admin, 'rate_limit_check', {
      p_bucket_key: bucketKey,
      p_bucket_type: bucketType,
      p_ip: ip,
      p_window_seconds: windowSeconds,
      p_limit: limit,
    });

    if (error || !data) {
      return fallbackCheck(bucketKey, ip, windowMs, limit);
    }

    const row = (Array.isArray(data) ? data[0] : data) as RateLimitRpcRow | undefined;
    if (!row) {
      return fallbackCheck(bucketKey, ip, windowMs, limit);
    }

    return {
      allowed: row.allowed,
      count: row.current_count,
      retryAfterSeconds: row.retry_after_seconds,
    };
  } catch {
    return fallbackCheck(bucketKey, ip, windowMs, limit);
  }
}
